import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from 'components/UnvUI'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import styles from './MilitaryStudentPage.module.scss'

const fmtDate = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('ru-RU')
  } catch {
    return '—'
  }
}

const emptyForm = {
  full_name: '',
  voenkomat: '',
  category: '',
  fitness: 'A',
  deferment_until: '',
}

const MilitaryStudentPage = () => {
  const { t } = useTranslation('common')
  const FITNESS = [
    { value: 'A', label: t('military.fit_a') },
    { value: 'B', label: t('military.fit_b') },
    { value: 'V', label: t('military.fit_v') },
    { value: 'G', label: t('military.fit_g') },
    { value: 'D', label: t('military.fit_d') },
  ]
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/students/military/')
      setList(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const openEdit = (row) => {
    setEditing(row)
    setForm({
      full_name: row.full_name || '',
      voenkomat: row.voenkomat || '',
      category: row.category || '',
      fitness: row.fitness || 'A',
      deferment_until: row.deferment_until || '',
    })
  }

  const save = async () => {
    if (!editing) return
    try {
      await $api.patch(`/students/military/${editing.id}/`, form)
      setNotify({ isOpen: true, message: t('military.saved'), type: 'success', sound: 'success' })
      setEditing(null)
      load()
    } catch {
      setNotify({ isOpen: true, message: t('military.save_error'), type: 'error', sound: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <h1 className={styles.title}>{t('military.title_page')}</h1>
      </div>

      <div className={`unv-card ${styles.content}`}>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        ) : error ? (
          <div className="unv-empty">{t('military.section_in_dev')}</div>
        ) : list.length === 0 ? (
          <div className="unv-empty">{t('military.no_records')}</div>
        ) : (
          <table className="unv-table">
            <thead>
              <tr>
                <th>{t('military.col_full_name')}</th>
                <th>{t('military.col_office')}</th>
                <th>{t('military.col_category')}</th>
                <th>{t('military.col_fitness')}</th>
                <th>{t('military.col_deferment_until')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td>{r.full_name || '—'}</td>
                  <td>{r.voenkomat || '—'}</td>
                  <td>{r.category || '—'}</td>
                  <td>{r.fitness || '—'}</td>
                  <td>{fmtDate(r.deferment_until)}</td>
                  <td>
                    <button className="unv-btn unv-btn--ghost unv-btn--sm" onClick={() => openEdit(r)}>
                      {t('military.edit_btn')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('military.modal_edit_record')}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label={t('military.field_full_name')}
            value={form.full_name}
            onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('military.field_office')}
            value={form.voenkomat}
            onChange={(e) => setForm((f) => ({ ...f, voenkomat: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('military.field_category')}
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            margin="normal"
          />
          <TextField
            select
            fullWidth
            label={t('military.field_fitness')}
            value={form.fitness}
            onChange={(e) => setForm((f) => ({ ...f, fitness: e.target.value }))}
            margin="normal"
          >
            {FITNESS.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            fullWidth
            label={t('military.field_deferment')}
            InputLabelProps={{ shrink: true }}
            value={form.deferment_until}
            onChange={(e) => setForm((f) => ({ ...f, deferment_until: e.target.value }))}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={save}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default MilitaryStudentPage
